import type { TaskStatus } from '@obras/shared';
import { DEFAULT_TIMEZONE, todayIn } from '@obras/shared';
import { CircleCheck, ListChecks, ListTodo, Plus, SearchX } from 'lucide-react';
import { useState } from 'react';

import { PageHeader } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { EmptyState, ErrorState, RefreshingBar } from '../components/ui/Feedback.js';
import { SearchInput } from '../components/ui/Field.js';
import { Tab, TabList } from '../components/ui/Tabs.js';
import { textoDeBusca } from '../features/tarefas/aparencia.js';
import { useTarefas } from '../features/tarefas/api.js';
import { ListaDeTarefas, ListaDeTarefasEsqueleto } from '../features/tarefas/ListaDeTarefas.js';
import { ModalDaTarefa, ModalNovaTarefa } from '../features/tarefas/TarefaModal.js';
import { useAuth } from '../lib/auth.js';
import { normalizarBusca } from '../lib/texto.js';

const ICON = { strokeWidth: 1.5 } as const;

type Aba = 'abertas' | 'concluidas';

/** O que cada aba mostra: as três situações em aberto, ou só a concluída. */
const SITUACOES_DA_ABA: Record<Aba, readonly TaskStatus[]> = {
  abertas: ['em_andamento', 'a_fazer', 'aguardando'],
  concluidas: ['concluida'],
};

/**
 * Tarefas (design.md §8, Tarefas; ADR 063).
 *
 * As tarefas em que a pessoa participa — criadas por ela ou em que foi
 * incluída —, em duas abas: **Em aberto**, o que está rolando, arrumado por
 * situação; e **Concluídas**, o que acabou. Criar é o botão do cabeçalho; abrir
 * uma tarefa mostra tudo dela num modal, onde cada mudança grava na hora. A
 * situação também troca direto da linha.
 *
 * A página abre sempre em "Em aberto": voltar e cair nas concluídas daria a
 * impressão de que o trabalho sumiu. A busca vale para as duas abas, e a
 * contagem de cada uma acompanha o que ela encontra.
 */
export function TarefasPage() {
  const { organization } = useAuth();
  const hoje = todayIn(organization?.timezone ?? DEFAULT_TIMEZONE);

  const [aba, setAba] = useState<Aba>('abertas');
  const [busca, setBusca] = useState('');
  const [criando, setCriando] = useState(false);
  const [aberturas, setAberturas] = useState(0);
  const [abertaId, setAbertaId] = useState<string | null>(null);

  const { data, isPending, isError, error, refetch, isFetching } = useTarefas();
  const todas = data?.items ?? [];
  const aberta = abertaId ? todas.find((tarefa) => tarefa.id === abertaId) : undefined;

  // A tarefa aberta que saiu da lista — excluída, ou a pessoa saiu dela — fecha o modal.
  if (abertaId !== null && data && !aberta) setAbertaId(null);

  function novaTarefa(): void {
    // Cada abertura monta um formulário novo, em branco.
    setAberturas((vezes) => vezes + 1);
    setCriando(true);
  }

  const termo = normalizarBusca(busca.trim());
  const encontradas = termo
    ? todas.filter((tarefa) => textoDeBusca(tarefa).includes(termo))
    : todas;
  const daAba = (qual: Aba) =>
    encontradas.filter((tarefa) => SITUACOES_DA_ABA[qual].includes(tarefa.status));
  const abertas = daAba('abertas');
  const concluidas = daAba('concluidas');
  const visiveis = aba === 'abertas' ? abertas : concluidas;

  function conteudo() {
    if (isError) {
      return (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      );
    }

    if (isPending) return <ListaDeTarefasEsqueleto />;

    if (todas.length === 0) {
      return (
        <Card square>
          <EmptyState
            icon={ListChecks}
            title="Nenhuma tarefa ainda"
            description="Crie uma tarefa, monte a equipe e liste o que precisa ser feito. Ela aparece aqui para você e para cada pessoa da equipe."
            action={
              <Button variant="primary" icon={<Plus {...ICON} size={16} />} onClick={novaTarefa}>
                Nova tarefa
              </Button>
            }
          />
        </Card>
      );
    }

    if (visiveis.length === 0) {
      return (
        <Card square>
          {termo ? (
            <EmptyState
              icon={SearchX}
              title={
                aba === 'abertas'
                  ? 'Nenhuma tarefa em aberto encontrada'
                  : 'Nenhuma tarefa concluída encontrada'
              }
              description={`Nada corresponde a "${busca.trim()}" nesta aba. A busca olha o título, a descrição, os itens do checklist e as pessoas.`}
              action={<Button onClick={() => setBusca('')}>Limpar busca</Button>}
            />
          ) : aba === 'abertas' ? (
            <EmptyState
              icon={CircleCheck}
              title="Nenhuma tarefa em aberto"
              description="Tudo o que estava em aberto foi concluído. Quando surgir algo novo, crie uma tarefa."
              action={
                <Button variant="primary" icon={<Plus {...ICON} size={16} />} onClick={novaTarefa}>
                  Nova tarefa
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={ListTodo}
              title="Nenhuma tarefa concluída ainda"
              description="Quando uma tarefa for concluída, ela sai de Em aberto e aparece aqui."
            />
          )}
        </Card>
      );
    }

    return (
      <ListaDeTarefas
        tarefas={visiveis}
        hoje={hoje}
        situacoes={SITUACOES_DA_ABA[aba]}
        onAbrir={setAbertaId}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Tarefas"
        description="As tarefas que você criou e aquelas em que foi incluído. Abra uma para ver o checklist, conversar com a equipe e mudar o que precisar."
        action={
          <Button variant="primary" icon={<Plus {...ICON} size={16} />} onClick={novaTarefa}>
            Nova tarefa
          </Button>
        }
      />

      <div className="max-w-xl">
        <SearchInput
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
          onClear={() => setBusca('')}
          placeholder="Buscar por título, item ou pessoa"
          aria-label="Buscar nas tarefas"
        />
      </div>

      {/* As duas abas: o que está rolando e o que acabou, cada uma com a sua contagem. */}
      <div className="mt-4">
        <TabList label="Tarefas em aberto ou concluídas">
          <Tab
            active={aba === 'abertas'}
            onClick={() => setAba('abertas')}
            icon={ListTodo}
            count={data ? abertas.length : undefined}
          >
            Em aberto
          </Tab>
          <Tab
            active={aba === 'concluidas'}
            onClick={() => setAba('concluidas')}
            icon={CircleCheck}
            count={data ? concluidas.length : undefined}
          >
            Concluídas
          </Tab>
        </TabList>
      </div>

      {/* Revalidação não desmonta a lista: só a barra fina. */}
      <div className="my-5">
        <RefreshingBar active={isFetching && !isPending} />
      </div>

      {conteudo()}

      <ModalNovaTarefa key={aberturas} open={criando} onClose={() => setCriando(false)} />
      <ModalDaTarefa tarefa={aberta} onClose={() => setAbertaId(null)} />
    </>
  );
}
